import { slicedToArray as _slicedToArray, createForOfIteratorHelper as _createForOfIteratorHelper } from '../../_virtual/_rollupPluginBabelHelpers.js';
import * as React from 'react';
import { useState, useRef, useCallback, useEffect } from 'react';
import { debounce } from '../utility/debounce.js';
import classNames from '../../node_modules/classnames/index.js';
import styles from './Tooltip.module.scss.js';
import { applyCommonProps } from '../utility/commonProps.js';
import { tooltipIsDocMode } from '../utility/utils.js';

var SHOW_DELAY = 500;
var HIDE_DELAY = 500;
/**
 * A customizable tooltip component that supports keyboard navigation
 * @param {TooltipProps} props - The properties for the Tooltip component
 * @returns {React.ReactElement} The Tooltip component
 */

var TooltipComponent = function TooltipComponent(props) {
  var _applyCommonProps = applyCommonProps(props, TooltipComponent),
    content = _applyCommonProps.content,
    _applyCommonProps$pla = _applyCommonProps.placement,
    placement = _applyCommonProps$pla === void 0 ? 'top' : _applyCommonProps$pla,
    _applyCommonProps$max = _applyCommonProps.maxWidth,
    maxWidth = _applyCommonProps$max === void 0 ? '400px' : _applyCommonProps$max,
    children = _applyCommonProps.children,
    externalTriggerRef = _applyCommonProps.triggerRef,
    _applyCommonProps$dis = _applyCommonProps.disabled,
    disabled = _applyCommonProps$dis === void 0 ? false : _applyCommonProps$dis,
    _applyCommonProps$cla = _applyCommonProps.className,
    className = _applyCommonProps$cla === void 0 ? '' : _applyCommonProps$cla,
    _applyCommonProps$zIn = _applyCommonProps.zIndex,
    zIndex = _applyCommonProps$zIn === void 0 ? 100 : _applyCommonProps$zIn;
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    isVisible = _useState2[0],
    setIsVisible = _useState2[1];
  var _useState3 = useState({
      x: 0,
      y: 0
    }),
    _useState4 = _slicedToArray(_useState3, 2),
    transform = _useState4[0],
    setTransform = _useState4[1];
  var isDocMode = tooltipIsDocMode();
  var _useState5 = useState(placement),
    _useState6 = _slicedToArray(_useState5, 2),
    currentPlacement = _useState6[0],
    setCurrentPlacement = _useState6[1];
  var internalTriggerRef = useRef(null);
  var tooltipRef = useRef(null);
  var effectiveTriggerRef = externalTriggerRef || internalTriggerRef;
  var calculatePosition = useCallback(function () {
    if (!effectiveTriggerRef.current || !tooltipRef.current) return null;
    var triggerRect = effectiveTriggerRef.current.getBoundingClientRect();
    var tooltipRect = tooltipRef.current.getBoundingClientRect();
    var viewportWidth = window.innerWidth;
    var viewportHeight = window.innerHeight;
    var placements = ['top', 'top-right', 'right', 'bottom-right', 'bottom', 'bottom-left', 'left', 'top-left'];
    var getOpposite = function getOpposite(p) {
      var opposites = {
        top: 'bottom',
        'top-right': 'bottom-left',
        right: 'left',
        'bottom-right': 'top-left',
        bottom: 'top',
        'bottom-left': 'top-right',
        left: 'right',
        'top-left': 'bottom-right'
      };
      return opposites[p];
    };
    var checkPlacement = function checkPlacement(p) {
      var top = 0;
      var left = 0;
      var gap = 4;
      var arrowSize = 6;
      var scrollTop = document.documentElement.scrollTop || document.body.scrollTop;
      var scrollLeft = document.documentElement.scrollLeft || document.body.scrollLeft;
      switch (p) {
        case 'top':
          top = triggerRect.top - tooltipRect.height - (arrowSize + gap);
          left = triggerRect.left + (triggerRect.width - tooltipRect.width) / 2;
          break;
        case 'top-right':
          top = triggerRect.top - tooltipRect.height - (arrowSize + gap);
          left = triggerRect.right - tooltipRect.width;
          break;
        case 'right':
          top = triggerRect.top + (triggerRect.height - tooltipRect.height) / 2;
          left = triggerRect.right + (arrowSize + gap);
          break;
        case 'bottom-right':
          top = triggerRect.bottom + (arrowSize + gap);
          left = triggerRect.right - tooltipRect.width;
          break;
        case 'bottom':
          top = triggerRect.bottom + (arrowSize + gap);
          left = triggerRect.left + (triggerRect.width - tooltipRect.width) / 2;
          break;
        case 'bottom-left':
          top = triggerRect.bottom + (arrowSize + gap);
          left = triggerRect.left;
          break;
        case 'left':
          top = triggerRect.top + (triggerRect.height - tooltipRect.height) / 2;
          left = triggerRect.left - tooltipRect.width - (arrowSize + gap);
          break;
        case 'top-left':
          top = triggerRect.top - tooltipRect.height - (arrowSize + gap);
          left = triggerRect.left;
          break;
      }

      // Adjust left and top in case there is a scrollable parent.
      left += scrollLeft;
      top += scrollTop;
      var valid = top >= 0 && left >= 0 && top + tooltipRect.height <= viewportHeight && left + tooltipRect.width <= viewportWidth;
      return {
        valid: valid,
        position: {
          top: top,
          left: left
        }
      };
    };
    var bestPlacement = placement;
    var bestPosition = checkPlacement(placement).position;
    if (!checkPlacement(placement).valid) {
      var oppositePlace = getOpposite(placement);
      var oppositeResult = checkPlacement(oppositePlace);
      if (oppositeResult.valid) {
        bestPlacement = oppositePlace;
        bestPosition = oppositeResult.position;
      } else {
        var _iterator = _createForOfIteratorHelper(placements),
          _step;
        try {
          for (_iterator.s(); !(_step = _iterator.n()).done;) {
            var p = _step.value;
            var result = checkPlacement(p);
            if (result.valid) {
              bestPlacement = p;
              bestPosition = result.position;
              break;
            }
          }
        } catch (err) {
          _iterator.e(err);
        } finally {
          _iterator.f();
        }
      }
    }
    return {
      newPlacement: bestPlacement,
      x: bestPosition.left,
      y: bestPosition.top
    };
  }, [placement, effectiveTriggerRef]);
  var updatePosition = useCallback(function () {
    var newPosition = calculatePosition();
    if (newPosition) {
      setTransform({
        x: newPosition.x,
        y: newPosition.y
      });
      setCurrentPlacement(newPosition.newPlacement);
    }
  }, [calculatePosition]);
  var throttledUpdatePosition = useCallback(function () {
  }, [updatePosition]);
  var debouncedShowTooltip = useCallback(debounce(function () {
    if (!disabled) {
      setIsVisible(true);
    }
  }, SHOW_DELAY), [disabled]);
  var debouncedHideTooltip = useCallback(debounce(function () {
    if (!disabled) {
      setIsVisible(false);
    }
  }, HIDE_DELAY), [disabled]);
  var handleKeyDown = useCallback(function (event) {
    if (event.key === 'Escape') {
      setIsVisible(false);
    }
  }, []);
  useEffect(function () {
    if (isVisible) {
      updatePosition();
      window.addEventListener('resize', throttledUpdatePosition);
      window.addEventListener('scroll', throttledUpdatePosition, true);
    }
    return function () {
      window.removeEventListener('resize', throttledUpdatePosition);
      window.removeEventListener('scroll', throttledUpdatePosition, true);
    };
  }, [isVisible, updatePosition, throttledUpdatePosition]);
  var triggerProps = {
    ref: effectiveTriggerRef,
    onMouseEnter: debouncedShowTooltip,
    onMouseLeave: debouncedHideTooltip,
    onFocus: debouncedShowTooltip,
    onBlur: debouncedHideTooltip,
    'aria-describedby': isVisible ? 'tooltip' : undefined,
    'aria-expanded': isVisible ? 'true' : 'false'
  };
  useEffect(function () {
    if (!(externalTriggerRef !== null && externalTriggerRef !== void 0 && externalTriggerRef.current)) return;
    var triggerElement = externalTriggerRef.current;
    triggerElement.addEventListener('mouseenter', debouncedShowTooltip);
    triggerElement.addEventListener('mouseleave', debouncedHideTooltip);
    triggerElement.addEventListener('focus', debouncedShowTooltip);
    triggerElement.addEventListener('blur', debouncedHideTooltip);
    return function () {
      triggerElement.removeEventListener('mouseenter', debouncedShowTooltip);
      triggerElement.removeEventListener('mouseleave', debouncedHideTooltip);
      triggerElement.removeEventListener('focus', debouncedShowTooltip);
      triggerElement.removeEventListener('blur', debouncedHideTooltip);
    };
  }, [externalTriggerRef, debouncedShowTooltip, debouncedHideTooltip]);
  var getStyleForPlacement = function getStyleForPlacement(placement) {
    var placementStyles = {
      top: styles.top,
      'top-right': styles.topRight,
      right: styles.right,
      'bottom-right': styles.bottomRight,
      bottom: styles.bottom,
      'bottom-left': styles.bottomLeft,
      left: styles.left,
      'top-left': styles.topLeft
    };
    return placementStyles[placement];
  };
  var tooltipClasses = classNames(styles.tooltip, !isDocMode ? getStyleForPlacement(currentPlacement) : styles.right, className);
  return /*#__PURE__*/React.createElement(React.Fragment, null, children && /*#__PURE__*/React.cloneElement(children, triggerProps), isVisible && /*#__PURE__*/React.createElement("div", {
    ref: tooltipRef,
    role: "tooltip",
    id: "tooltip",
    className: tooltipClasses,
    style: {
      maxWidth: maxWidth,
      transform: !isDocMode ? "translate(".concat(transform.x, "px, ").concat(transform.y, "px)") : "translate(92px, 3px)",
      zIndex: zIndex
    },
    onKeyDown: handleKeyDown
  }, /*#__PURE__*/React.createElement("div", {
    className: styles.tooltipContent
  }, content)));
};
TooltipComponent.displayName = 'Tooltip';
var Tooltip = /*#__PURE__*/React.memo(TooltipComponent);

export { Tooltip };
